import React, { useContext, useEffect, useRef, useState } from "react";
import { formatStringWithPlaceholders, hasXSSCharacters, isEmail, isFullNameValid } from "../../../../helpers/Utils";
import ICountry from "../../../../models/Country.interface";
import { IReviewAndPaySenderInfoStepModel, ReviewAndPaySenderInfoStepModel } from "../../../../models/buy-giftcard/review-pay/ReviewAndPaySenderInfoStep.model";
import { BuyGiftCardStateEnum } from "../../../../models/enums/BuyGiftCardStateEnum";
import { IBuyGiftCardStepProps } from "../../../../models/interfaces/BuyGiftCardStep.interface";
import DataService from "../../../../services/DataService.service";
import { AppSettingContext } from "../../../Application";
import LoadingComponent from "../../../shared/LoadingComponent";

interface IReviewAndPaySenderInfoStepProps extends IBuyGiftCardStepProps {
    stepModel: IReviewAndPaySenderInfoStepModel;

    handleSubmit(stepModel: IReviewAndPaySenderInfoStepModel): void;
}

const ReviewAndPaySenderInfoStep = (props: IReviewAndPaySenderInfoStepProps) => {
    const { labels } = useContext(AppSettingContext);
    const checkboxRef = useRef(null);

    //#region STATE PROPERTIES

    const [stepModel, setStepModel] = useState<IReviewAndPaySenderInfoStepModel>({
        usePersonalDetails: props.stepModel.usePersonalDetails,
        useBusinessDetails: props.stepModel.useBusinessDetails,
        personDetails: props.stepModel.personDetails,
        businessDetails: props.stepModel.businessDetails
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isModelValid, setIsModelValid] = useState<boolean>();
    const [modelErrors, setModelErrors] = useState({
        personDetails: {
            fullName: '',
            email: ''
        },
        businessDetails: {
            fullName: '',
            email: '',
            companyName: '',
            tradingAs: '',
            businessType: '',
            vat: '',
            cro: '',
            businessAddress: {
                country: '',
                city: '',
                postcode: '',
                addressLine1: ''
            }
        }
    });
    const [countries, setCountries] = useState<ICountry[]>([] as ICountry[]);

    //#endregion

    //#region USE EFFECT HOOKS

    useEffect(() => {
        getCountries();

        if (!stepModel.businessDetails.businessAddress.country) {
            setStepModel({
                ...stepModel,
                businessDetails: {
                    ...stepModel.businessDetails,
                    businessAddress: {
                        ...stepModel.businessDetails.businessAddress,
                        country: 'IE'
                    }
                }
            });
        }
    }, []);

    useEffect(() => {
        setIsModelValid(isValid());
    }, [stepModel]);

    useEffect(() => {
        setIsLoading(props.isSaving);
    }, [props.isSaving]);

    useEffect(() => {
        checkboxRef.current.checked = props.activeStep === BuyGiftCardStateEnum.REVIEW_PAY_SENDER_INFO;
    }, [props.activeStep]);

    //#endregion

    //#region HANDLERS

    const handleExpandBoxCheck = (): void => {
        if (checkboxRef.current.checked && props.activeStep !== BuyGiftCardStateEnum.REVIEW_PAY_SENDER_INFO) {
            props.handleSetActiveStep(BuyGiftCardStateEnum.REVIEW_PAY_SENDER_INFO);
        }
    };

    const handleDetailsTypeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { id, checked } = event.target;

        if (id === "usePersonalDetails") {
            setStepModel({
                ...stepModel,
                usePersonalDetails: checked,
                useBusinessDetails: !checked
            });
        } else if (id === "useBusinessDetails") {
            setStepModel({
                ...stepModel,
                usePersonalDetails: !checked,
                useBusinessDetails: checked
            });
        }
    };

    const handlePersonDetailsChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = event.target;

        if (hasXSSCharacters(value)) {
            event.preventDefault();
            return;
        }

        let errMsg = '';

        if (name === 'fullName') {
            if (!value || !value.trim()) {
                errMsg = formatStringWithPlaceholders(labels.get("GiftCard.App.V2.Shared.ErrorMessages.FieldRequired"), "Full name");
            } else if (!isFullNameValid(value.trim())) {
                errMsg = "Invalid full name";
            }
        } else if (name === 'email') {
            if (!value || !value.trim()) {
                errMsg = formatStringWithPlaceholders(labels.get("GiftCard.App.V2.Shared.ErrorMessages.FieldRequired"), "Email");
            } else if (!isEmail(value)) {
                errMsg = labels.get("GiftCard.App.V2.ReviewAndPaySender.Error.EmailInvalid");
            }
        }

        setModelErrors({
            ...modelErrors,
            personDetails: {
                ...modelErrors.personDetails,
                [name]: errMsg
            }
        });

        setStepModel({
            ...stepModel,
            personDetails: {
                ...stepModel.personDetails,
                [name]: value
            }
        });
    };

    const handleBusinessDetailsChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = event.target;

        if (hasXSSCharacters(value)) {
            event.preventDefault();
            return;
        }

        let errMsg = '';
        const requiredTextFields = ['fullName', 'companyName'];

        if (name === 'email') {
            if (!value || !value.trim()) {
                errMsg = formatStringWithPlaceholders(labels.get("GiftCard.App.V2.Shared.ErrorMessages.FieldRequired"), "Email");
            } else if (!isEmail(event.target.value)) {
                errMsg = labels.get("GiftCard.App.V2.ReviewAndPaySender.Error.EmailInvalid");
            }
        } else if (requiredTextFields.includes(name) && (!value || !value.trim())) {
            let errSubstitution = '';

            switch (name) {
                case 'fullName': errSubstitution = "Full name";
                    break;
                case 'companyName': errSubstitution = "Company name";
                    break;
                default:
                    break;
            }

            errMsg = formatStringWithPlaceholders(labels.get("GiftCard.App.V2.Shared.ErrorMessages.FieldRequired"), errSubstitution);
        } else if (name === 'fullName' && !isFullNameValid(value.trim())) {
            errMsg = 'Invalid full name';
        }

        if (name !== 'tradingAs' && name !== 'vat' && name !== 'cro') {
            setModelErrors({
                ...modelErrors,
                businessDetails: {
                    ...modelErrors.businessDetails,
                    [name]: errMsg
                }
            });
        }

        setStepModel({
            ...stepModel,
            businessDetails: {
                ...stepModel.businessDetails,
                [name]: value
            }
        });
    };

    const handleBusinessTypeChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        if (hasXSSCharacters(event.target.value)) {
            event.preventDefault();
            return;
        }

        let errMsg = '';
        if (!event.target.value || !event.target.value.trim()) {
            errMsg = formatStringWithPlaceholders(labels.get("GiftCard.App.V2.Shared.ErrorMessages.FieldRequired"), "Business type");
        }

        setModelErrors({
            ...modelErrors,
            businessDetails: {
                ...modelErrors.businessDetails,
                businessType: errMsg
            }
        });

        setStepModel({
            ...stepModel,
            businessDetails: {
                ...stepModel.businessDetails,
                businessType: event.target.value
            }
        });
    };

    const handleBusinessAddressChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = event.target;

        if (hasXSSCharacters(value)) {
            event.preventDefault();
            return;
        }

        let errMsg = '';

        if (name === 'country' && (!value || !value.trim())) {
            errMsg = formatStringWithPlaceholders(labels.get("GiftCard.App.V2.Shared.ErrorMessages.FieldRequired"), "Country");
        } else if (name === 'city' && (!value || !value.trim())) {
            errMsg = formatStringWithPlaceholders(labels.get("GiftCard.App.V2.Shared.ErrorMessages.FieldRequired"), "City");
        } else if (name === 'addressLine1' && (!value || !value.trim())) {
            errMsg = formatStringWithPlaceholders(labels.get("GiftCard.App.V2.Shared.ErrorMessages.FieldRequired"), "Address Line 1");
        } else if (name === 'postcode' && value && value.trim() && value.trim().length < 3) {
            errMsg = "Invalid postcode";
        }

        if (name !== 'county' && name !== 'addressLine2' && name !== 'addressLine3') {
            setModelErrors({
                ...modelErrors,
                businessDetails: {
                    ...modelErrors.businessDetails,
                    businessAddress: {
                        ...modelErrors.businessDetails.businessAddress,
                        [name]: errMsg
                    }
                }
            });
        }

        setStepModel({
            ...stepModel,
            businessDetails: {
                ...stepModel.businessDetails,
                businessAddress: {
                    ...stepModel.businessDetails.businessAddress,
                    [name]: value
                }
            }
        });
    };

    const handleBusinessCountryChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        if (hasXSSCharacters(event.target.value)) {
            event.preventDefault();
            return;
        }

        let errMsg = '';

        if (!event.target.value || !event.target.value.trim()) {
            errMsg = formatStringWithPlaceholders(labels.get("GiftCard.App.V2.Shared.ErrorMessages.FieldRequired"), "Country");
        } else if (!countries.map(c => c.code).includes(event.target.value)) {
            errMsg = "Invalid country";
        }

        setModelErrors({
            ...modelErrors,
            businessDetails: {
                ...modelErrors.businessDetails,
                businessAddress: {
                    ...modelErrors.businessDetails.businessAddress,
                    country: errMsg
                }
            }
        });

        setStepModel({
            ...stepModel,
            businessDetails: {
                ...stepModel.businessDetails,
                businessAddress: {
                    ...stepModel.businessDetails.businessAddress,
                    country: event.target.value
                }
            }
        });
    };

    const handleSaveAndContinue = (event: React.MouseEvent<HTMLButtonElement>): void => {
        event.preventDefault();

        if (!isValid()) {
            return;
        }

        props.handleSubmit(stepModel);
        checkboxRef.current.checked = false;
    };

    //#endregion

    //#region COMMON HELPERS

    const getCountries = async () => {
        const countriesJson = await DataService.getCountries(document.documentElement.lang ?? "en-GB");
        const countriesArr = [];

        for (let i = 0; i < countriesJson.length; i++) {
            countriesArr.push({
                name: countriesJson[i].name,
                code: countriesJson[i].code
            } as ICountry);
        }

        setCountries(countriesArr);
    };

    const isValid = (): boolean => {
        return ReviewAndPaySenderInfoStepModel.isValid(stepModel, countries);
    };

    //#endregion

    return (
        <div className="box your-info">
            <input
                type="checkbox"
                name="your-info"
                id="your-info"
                ref={checkboxRef}
                onChange={handleExpandBoxCheck}
                defaultChecked={true}
            />
            <div className="action-card">
                <h4 className="box-heading">
                    <i className="box-icon">
                        <img src="/static/options-card-page/options-card-icon-03.png" alt="icon 03" />
                    </i>
                    {labels.get("GiftCard.App.V2.ReviewAndPaySender.Title")}
                    <i
                        className="box-arrow"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                            checkboxRef.current.checked = !checkboxRef.current.checked;
                            handleExpandBoxCheck();
                        }}
                    ></i>
                </h4>
                {props.activeStep === BuyGiftCardStateEnum.REVIEW_PAY_SENDER_INFO && (
                    <div className="show">
                        {isLoading ? (
                            <LoadingComponent />
                        ) : (
                            <div className="slide-content">
                                <h5>
                                    {labels.get("GiftCard.App.V2.ReviewAndPaySender.Subtitle1")}
                                </h5>
                                <ul style={{ display: "flex", gap: "22px", marginTop: '12px' }}>
                                    <li style={{ display: "flex", gap: "10px" }}>
                                        <input
                                            type="radio"
                                            name="send-options"
                                            id="usePersonalDetails"
                                            checked={stepModel.usePersonalDetails}
                                            onChange={handleDetailsTypeChange}
                                        />
                                        <label htmlFor="send-now">{labels.get("GiftCard.App.V2.ReviewAndPaySender.Button.UsePersonalDetails")}</label>
                                    </li>
                                    <li style={{ display: "flex", gap: "10px" }}>
                                        <input
                                            type="radio"
                                            name="sender-options"
                                            id="useBusinessDetails"
                                            checked={stepModel.useBusinessDetails}
                                            onChange={handleDetailsTypeChange}
                                        />
                                        <label htmlFor="send-later">{labels.get("GiftCard.App.V2.ReviewAndPaySender.Button.UseBusinessDetails")}</label>
                                    </li>
                                </ul>

                                {stepModel.usePersonalDetails && (
                                    <>
                                        <p style={{ color: 'rgb(90 90 90)', marginTop: '8px' }}>
                                            {labels.get("GiftCard.App.V2.ReviewAndPaySender.Subtitle2")}
                                        </p>
                                        <h5 style={{ marginBottom: '-8px' }}>
                                            {labels.get("GiftCard.App.V2.ReviewAndPaySender.Info.Purchaser")}
                                        </h5>
                                        <div className="flex-container">
                                            <div className="form-field">
                                                <input
                                                    type="text"
                                                    name="fullName"
                                                    id="fullName"
                                                    placeholder={labels.get("GiftCard.App.V2.ReviewAndPaySender.Placeholder.FullName")}
                                                    className={`${modelErrors.personDetails.fullName ? "react-input-error" : ""}`}
                                                    value={stepModel.personDetails.fullName ?? ""}
                                                    maxLength={100}
                                                    onChange={handlePersonDetailsChange}
                                                />

                                                {modelErrors.personDetails.fullName && (
                                                    <i className="small-fs info react-input-error-message">
                                                        {modelErrors.personDetails.fullName}
                                                    </i>
                                                )}
                                            </div>
                                            <div className="form-field">
                                                <input
                                                    type="text"
                                                    name="email"
                                                    id="email"
                                                    placeholder={labels.get("GiftCard.App.V2.ReviewAndPaySender.Placeholder.Email")}
                                                    className={modelErrors.personDetails.email ? "react-input-error" : ""}
                                                    value={stepModel.personDetails.email ?? ""}
                                                    onChange={handlePersonDetailsChange}
                                                />

                                                {modelErrors.personDetails.email && (
                                                    <i className="small-fs info react-input-error-message">
                                                        {modelErrors.personDetails.email}
                                                    </i>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}

                                {stepModel.useBusinessDetails && (
                                    <>
                                        <p style={{ color: 'rgb(90 90 90)', marginTop: '8px' }}>
                                            {labels.get("GiftCard.App.V2.ReviewAndPaySender.Subtitle2")}
                                        </p>
                                        <h5 style={{ marginBottom: '-8px' }}>
                                            {labels.get("GiftCard.App.V2.ReviewAndPaySender.Info.Purchaser")}
                                        </h5>
                                        <div className="flex-container">
                                            <div className="form-field">
                                                <input
                                                    type="text"
                                                    name="fullName"
                                                    id="fullName"
                                                    placeholder={labels.get("GiftCard.App.V2.ReviewAndPaySender.Placeholder.FullName")}
                                                    className={`${modelErrors.businessDetails.fullName ? "react-input-error" : ""}`}
                                                    value={stepModel.businessDetails.fullName ?? ""}
                                                    maxLength={100}
                                                    onChange={handleBusinessDetailsChange}
                                                />

                                                {modelErrors.businessDetails.fullName && (
                                                    <i className="small-fs info react-input-error-message">
                                                        {modelErrors.businessDetails.fullName}
                                                    </i>
                                                )}
                                            </div>
                                            <div className="form-field">
                                                <input
                                                    type="text"
                                                    name="email"
                                                    id="email"
                                                    placeholder={labels.get("GiftCard.App.V2.ReviewAndPaySender.Placeholder.Email")}
                                                    className={modelErrors.businessDetails.email ? "react-input-error" : ""}
                                                    value={stepModel.businessDetails.email ?? ""}
                                                    onChange={handleBusinessDetailsChange}
                                                />

                                                {modelErrors.businessDetails.email && (
                                                    <i className="small-fs info react-input-error-message">
                                                        {modelErrors.businessDetails.email}
                                                    </i>
                                                )}
                                            </div>
                                        </div>
                                        <h5 style={{ marginBottom: '-8px' }}>
                                            {labels.get("GiftCard.App.V2.ReviewAndPaySender.Info.BusinessDetails")}
                                        </h5>
                                        <div className="flex-container">
                                            <div className="form-field" style={{ flex: '1 1 100%' }}>
                                                <input
                                                    type="text"
                                                    className={modelErrors.businessDetails.companyName ? "react-input-error" : ""}
                                                    name="companyName"
                                                    id="companyName"
                                                    placeholder={labels.get("GiftCard.App.V2.ReviewAndPaySender.Placeholder.CompanyName")}
                                                    value={stepModel.businessDetails.companyName ?? ""}
                                                    onChange={handleBusinessDetailsChange}
                                                />

                                                {modelErrors.businessDetails.companyName && (
                                                    <i className="small-fs info react-input-error-message">
                                                        {modelErrors.businessDetails.companyName}
                                                    </i>
                                                )}
                                            </div>
                                            <div className="form-field">
                                                <input
                                                    type="text"
                                                    name="tradingAs"
                                                    id="tradingAs"
                                                    placeholder={labels.get("GiftCard.App.V2.ReviewAndPaySender.Placeholder.TradingAs")}
                                                    value={stepModel.businessDetails.tradingAs ?? ""}
                                                    onChange={handleBusinessDetailsChange}
                                                />
                                            </div>
                                            <div className="form-field">
                                                <select
                                                    className={modelErrors.businessDetails.businessType ? "react-input-error" : ""}
                                                    name="businessType"
                                                    id="businessType"
                                                    value={stepModel.businessDetails.businessType ?? ""}
                                                    onChange={handleBusinessTypeChange}
                                                >
                                                    {(() => {
                                                        const options = [];

                                                        options.push(
                                                            <option key="0" value="">
                                                                {labels.get("GiftCard.App.V2.ReviewAndPaySender.Placeholder.BusinessType")}
                                                            </option>
                                                        );

                                                        const optionsFromUmbraco = labels
                                                            .get("GiftCard.App.V2.ReviewAndPaySender.BusinessTypes")
                                                            .split(";")
                                                            .map(x => x.trim());

                                                        for (let i = 0; i < optionsFromUmbraco.length; i++) {
                                                            options.push(
                                                                <option key={`btype_${i + 1}`} value={optionsFromUmbraco[i]}>
                                                                    {optionsFromUmbraco[i]}
                                                                </option>
                                                            );
                                                        }

                                                        return options;
                                                    })()}
                                                </select>

                                                {modelErrors.businessDetails.businessType && (
                                                    <i className="small-fs info react-input-error-message">
                                                        {modelErrors.businessDetails.businessType}
                                                    </i>
                                                )}
                                            </div>
                                            <div className="form-field">
                                                <input
                                                    type="text"
                                                    name="vat"
                                                    id="vat"
                                                    placeholder={labels.get("GiftCard.App.V2.ReviewAndPaySender.Placeholder.Vat")}
                                                    value={stepModel.businessDetails.vat ?? ""}
                                                    onChange={handleBusinessDetailsChange}
                                                />
                                            </div>
                                            <div className="form-field">
                                                <input
                                                    type="text"
                                                    name="cro"
                                                    id="cro"
                                                    placeholder={labels.get("GiftCard.App.V2.ReviewAndPaySender.Placeholder.Cro")}
                                                    value={stepModel.businessDetails.cro ?? ""}
                                                    onChange={handleBusinessDetailsChange}
                                                />
                                            </div>
                                        </div>
                                        <h5 style={{ marginBottom: '-8px' }}>
                                            {labels.get("GiftCard.App.V2.ReviewAndPaySender.Info.Address")}
                                        </h5>
                                        <div className="flex-container">
                                            <div className="form-field">
                                                <select
                                                    className={modelErrors.businessDetails.businessAddress.country ? "react-input-error" : ""}
                                                    name="country"
                                                    id="country"
                                                    value={stepModel.businessDetails.businessAddress.country ?? ""}
                                                    onChange={handleBusinessCountryChange}
                                                >
                                                    {countries.map((country, index) => (
                                                        <option key={`country_${index}`} value={country.code}>
                                                            {country.name}
                                                        </option>
                                                    ))}
                                                </select>

                                                {modelErrors.businessDetails.businessAddress.country && (
                                                    <i className="small-fs info react-input-error-message">
                                                        {modelErrors.businessDetails.businessAddress.country}
                                                    </i>
                                                )}
                                            </div>

                                            <div className="form-field">
                                                <input
                                                    type="text"
                                                    name="county"
                                                    id="county"
                                                    placeholder={labels.get("GiftCard.App.V2.ReviewAndPaySender.Placeholder.County")}
                                                    value={stepModel.businessDetails.businessAddress.county ?? ""}
                                                    onChange={handleBusinessAddressChange}
                                                />
                                            </div>
                                            <div className="form-field">
                                                <input
                                                    type="text"
                                                    name="city"
                                                    id="city"
                                                    className={`${modelErrors.businessDetails.businessAddress.city ? "react-input-error" : ""}`}
                                                    placeholder={labels.get("GiftCard.App.V2.ReviewAndPaySender.Placeholder.City")}
                                                    value={stepModel.businessDetails.businessAddress.city ?? ""}
                                                    onChange={handleBusinessAddressChange}
                                                />

                                                {modelErrors.businessDetails.businessAddress.city && (
                                                    <i className="small-fs info react-input-error-message">
                                                        {modelErrors.businessDetails.businessAddress.city}
                                                    </i>
                                                )}
                                            </div>
                                            <div className="form-field">
                                                <input
                                                    type="text"
                                                    name="postcode"
                                                    id="postcode"
                                                    className={`${modelErrors.businessDetails.businessAddress.city ? "react-input-error" : ""}`}
                                                    placeholder={labels.get("GiftCard.App.V2.ReviewAndPaySender.Placeholder.Postcode")}
                                                    value={stepModel.businessDetails.businessAddress.postcode ?? ""}
                                                    onChange={handleBusinessAddressChange}
                                                />

                                                {modelErrors.businessDetails.businessAddress.postcode && (
                                                    <i className="small-fs info react-input-error-message">
                                                        {modelErrors.businessDetails.businessAddress.postcode}
                                                    </i>
                                                )}
                                            </div>
                                            <div className="form-field">
                                                <input
                                                    type="text"
                                                    className={`${modelErrors.businessDetails.businessAddress.addressLine1 ? "react-input-error" : ""}`}
                                                    name="addressLine1"
                                                    id="addressLine1"
                                                    placeholder={labels.get("GiftCard.App.V2.ReviewAndPaySender.Placeholder.AddressLine1")}
                                                    value={stepModel.businessDetails.businessAddress.addressLine1 ?? ""}
                                                    onChange={handleBusinessAddressChange}
                                                />

                                                {modelErrors.businessDetails.businessAddress.addressLine1 && (
                                                    <i className="small-fs info react-input-error-message">
                                                        {modelErrors.businessDetails.businessAddress.addressLine1}
                                                    </i>
                                                )}
                                            </div>
                                            <div className="form-field">
                                                <input
                                                    type="text"
                                                    name="addressLine2"
                                                    id="addressLine2"
                                                    placeholder={labels.get("GiftCard.App.V2.ReviewAndPaySender.Placeholder.AddressLine2")}
                                                    value={stepModel.businessDetails.businessAddress.addressLine2 ?? ""}
                                                    onChange={handleBusinessAddressChange}
                                                />
                                            </div>
                                            <div className="form-field">
                                                <input
                                                    type="text"
                                                    name="addressLine3"
                                                    id="addressLine3"
                                                    placeholder={labels.get("GiftCard.App.V2.ReviewAndPaySender.Placeholder.AddressLine3")}
                                                    value={stepModel.businessDetails.businessAddress.addressLine3 ?? ""}
                                                    onChange={handleBusinessAddressChange}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}

                                <div className="buttons">
                                    <button
                                        className="btn btn__full_width btn__primary large-fs btn__large"
                                        onClick={handleSaveAndContinue}
                                        disabled={!isModelValid}
                                    >
                                        {labels.get("GiftCard.App.V2.ReviewAndPaySender.Button.Submit")}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div >
    );
};

export default ReviewAndPaySenderInfoStep;